import PropTypes from 'prop-types';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar, InputBase, IconButton } from '@mui/material';
import { Search, DarkModeOutlined, LightModeOutlined } from '@mui/icons-material';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
import useSettings from '../../../hooks/useSettings';
// config
import { HEADER, NAVBAR } from '../../../config';
// components
import Iconify from '../../../components/Iconify';
import { IconButtonAnimate } from '../../../components/animate';
//
import AccountPopover from './AccountPopover';
import NotificationsPopover from './NotificationsPopover';

// ----------------------------------------------------------------------

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout',
})(({ isCollapse, isOffset, verticalLayout, theme }) => ({
  backgroundColor: theme.palette.background.paper,
  boxShadow: 'none',
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('lg')]: {
    height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
    }),
    ...(isOffset && {
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
    }),
    ...(verticalLayout && {
      width: '100%',
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
      backgroundColor: theme.palette.background.default,
    }),
  },
}));

const SearchInputStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.background.neutral,
  borderRadius: '24px',
  padding: theme.spacing(0, 2),
  width: 280,
  height: 40,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'border-color 0.2s',
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
  }
}));

// ----------------------------------------------------------------------

DashboardHeader.propTypes = {
  onOpenSidebar: PropTypes.func,
  isCollapse: PropTypes.bool,
  verticalLayout: PropTypes.bool,
};

export default function DashboardHeader({ onOpenSidebar, isCollapse = false, verticalLayout = false }) {
  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;
  const isDesktop = useResponsive('up', 'lg');
  const { themeMode, onToggleMode } = useSettings();

  return (
    <RootStyle isCollapse={isCollapse} isOffset={isOffset} verticalLayout={verticalLayout} sx={{ bgcolor: 'background.paper', borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
      <Toolbar
        sx={{
          minHeight: '100% !important',
          px: { lg: 5 },
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          {!isDesktop && (
            <IconButtonAnimate onClick={onOpenSidebar} sx={{ mr: 1, color: 'text.primary' }}>
              <Iconify icon="eva:menu-2-fill" />
            </IconButtonAnimate>
          )}

          {isDesktop && (
            <SearchInputStyle>
              <Search sx={{ color: 'text.disabled', width: 20, height: 20, mr: 1.25 }} />
              <InputBase 
                placeholder="Search services, history..." 
                sx={{ 
                   flex: 1, 
                   fontSize: '0.85rem', 
                   fontWeight: 700,
                   color: 'text.primary',
                   '& input::placeholder': { color: 'text.disabled', opacity: 1 } 
                }} 
              />
            </SearchInputStyle>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1.5}>
          {/* Dark Mode / Light Mode Toggle Button styled matching the mockup */}
          <IconButton
            onClick={onToggleMode}
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
                color: 'text.primary',
              }
            }}
          >
            {themeMode === 'dark' ? (
              <LightModeOutlined sx={{ width: 20, height: 20 }} />
            ) : (
              <DarkModeOutlined sx={{ width: 20, height: 20 }} />
            )}
          </IconButton>

          <NotificationsPopover />
          
          <AccountPopover />
        </Stack>
      </Toolbar>
    </RootStyle>
  );
}
